/**
 * Datasource local Clients: abstração + implementação localforage.
 */

import localforage from 'localforage';
import { Client } from '@/core/domain';

const CACHE_KEY_PREFIX = 'retornaar:clients';

export interface ClientLocalDataSource {
  getById(id: string): Promise<Client | null>;
  getAllByUserId(userId: string): Promise<Client[]>;
  create(client: Client): Promise<Client>;
  update(id: string, client: Partial<Client>): Promise<Client>;
  delete(id: string): Promise<void>;
  clear(userId?: string): Promise<void>;
}

const store = localforage.createInstance({ name: 'retornaar', storeName: 'clients' });

export class ClientLocalForageDataSource implements ClientLocalDataSource {
  private cacheKey(userId: string) {
    return `${CACHE_KEY_PREFIX}:${userId}`;
  }

  private async getMap(userId: string): Promise<Record<string, Client>> {
    const raw = await store.getItem<Record<string, Client>>(this.cacheKey(userId));
    return raw ?? {};
  }

  private async setMap(userId: string, map: Record<string, Client>): Promise<void> {
    await store.setItem(this.cacheKey(userId), map);
  }

  async getById(id: string): Promise<Client | null> {
    const keys = await store.keys();
    const clientKeys = keys.filter((k) => typeof k === 'string' && k.startsWith(CACHE_KEY_PREFIX));
    for (const key of clientKeys) {
      const map = await store.getItem<Record<string, Client>>(key);
      if (map?.[id]) return map[id];
    }
    return null;
  }

  async getAllByUserId(userId: string): Promise<Client[]> {
    const map = await this.getMap(userId);
    
    return Object.values(map);
  }

  async create(client: Client): Promise<Client> {
    const map = await this.getMap(client.user_id);
    map[client.id] = client;
    await this.setMap(client.user_id, map);
    return client;
  }

  async update(id: string, client: Partial<Client>): Promise<Client> {
    const c = await this.getById(id);
    if (!c) throw new Error('Client not found');
    const map = await this.getMap(c.user_id);
    const current = map[id];
    if (!current) throw new Error('Client not found');
    const updated = { ...current, ...client, id };
    map[id] = updated;
    await this.setMap(c.user_id, map);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const c = await this.getById(id);
    if (!c) return;
    const map = await this.getMap(c.user_id);
    delete map[id];
    await this.setMap(c.user_id, map);
  }

  async clear(userId?: string): Promise<void> {
    if (userId) {
      await store.removeItem(this.cacheKey(userId));
    } else {
      const keys = await store.keys();
      for (const key of keys) {
        if (typeof key === 'string' && key.startsWith(CACHE_KEY_PREFIX)) {
          await store.removeItem(key);
        }
      }
    }
  }
}
